import Spine from '@finviz/spine'
import { isSameDay, isSameMonth, isSameWeek } from 'date-fns'
import omit from 'lodash.omit'
import queryString from 'query-string'

import { Instrument, ObjectHash, RootChartConfigObject, TodoObjectHashAnyType } from '../../../types/shared'
import {
  IntradayTimeframeInterval,
  QuoteFinancialAttachmentType,
  TIMEFRAME,
  TIMEFRAME_SECONDS,
} from '../../constants/common'
import type { ICOT } from '../../indicators/cot'
import { convertLocalToNyTime, dateFromUnixTimestamp, dateStringFromDate } from '../../utils'
import { getIsAbortError } from '../../utils/abort_controller'
import { IDividends, IEarnings, ISplit } from '../../utils/chart-events-utils'
import fetchApi, { NotFoundError } from '../../utils/fetch_api'
import { getIsSSr } from '../../utils/helpers'
import { getFutureTradingDates, getHistoryTradingDates } from '../../utils/stockDates'
import { deleteCachedQuote, getCachedQuote, setCachedQuote } from '../quoteCache'
import { getRawTickerForInstrument } from '../utils'
import { FUTURES_FORCE_REFETCH_TIMEOUT, QuoteFetchOptions, QuoteFetchType, QuoteUrlOptions } from './constants'
import { FinancialAttachmentsData } from './financialAttachmentsData'

type TodoPattern = TodoObjectHashAnyType

interface QuoteInternalOptions {
  cachePredicate?: (quote: Quote) => boolean
  fetchNewDataOnCachedQuote?: boolean
}

interface UrlOptions extends Omit<QuoteFetchOptions, 'financialAttachments'> {
  ticker: string
  instrument: string
  timeframe: string
  financialAttachments?: string
  dataId?: string
}

type ChartEventDataRaw = IEarnings | IDividends | ISplit

export interface DataResponse {
  COTs: COTData
  name: string
  ticker: string
  instrument: string
  interval: number
  timeframe: string
  open: number[]
  high: number[]
  low: number[]
  close: number[]
  date: number[]
  volume: number[]
  lastOpen: number
  lastHigh: number
  lastLow: number
  lastClose: number
  lastDate: number
  lastTime: string | null
  lastVolume: number
  dataId: string
  prevClose: number
  afterClose: number
  afterChange: number
  afterTime: string | null
  drawMinutesPerDay: number
  marketStartMinutes: number
  premarketLengthMinutes: number
  aftermarketLengthMinutes: number
  aftermarket: number
  premarket: number
  hasPatterns: boolean
  patterns: Array<TodoPattern>
  patternsMinRange: number
  patternsMaxRange: number
  relativeVolume: number
  isIntraday: boolean
  noNewData?: boolean
  updateOhlcVersion?: number
  chartEvents?: ChartEventDataRaw[]
  financialAttachmentsData?: FinancialAttachmentsData
}

interface COTData {
  [code: string]: ICOT
}

const COTProps = ['commercialHedgers', 'largeTraders', 'smallTraders'] as const

const DEFAULT_VIRTUAL_TRADED_DATES = {
  nextCacheKey: null,
  prevCacheKey: null,
  next: [],
  prev: [],
}

const getEmptyQuote = () => ({
  open: [],
  high: [],
  low: [],
  close: [],
  volume: [],
  date: [],
  fetchedAt: -1,
  lastRefetch: -1,
  fetchingState: [],
  is404: false,
  hasError: false,
  COTs: undefined,
  name: undefined,
  drawMinutesPerDay: undefined,
  marketStartMinutes: undefined,
  premarketLengthMinutes: undefined,
  aftermarketLengthMinutes: undefined,
  patterns: [],
  patternsMinRange: undefined,
  patternsMaxRange: undefined,
  relativeVolume: undefined,
  lastOpen: undefined,
  lastHigh: undefined,
  lastLow: undefined,
  lastClose: undefined,
  lastVolume: undefined,
  dataId: undefined,
  lastDate: undefined,
  prevClose: undefined,
  afterClose: undefined,
  afterChange: undefined,
  chartEvents: [],
  financialAttachments: [],
  financialAttachmentsData: null,
  hasReachedEnd: false,
  _virtualTradedDates: { ...DEFAULT_VIRTUAL_TRADED_DATES },
})

interface ICacheAvailabilityCheckFunctionsArguments {
  quote: Quote
  options?: QuoteFetchOptions & QuoteInternalOptions
}

const doesQuoteAndOptionsMatch = ({ quote, options }: ICacheAvailabilityCheckFunctionsArguments) =>
  // Only intraday quotes have afterhours
  (!quote.isIntraday || (!quote.premarket === !options?.premarket && !quote.aftermarket === !options?.aftermarket)) &&
  (!options?.patterns || !quote.hasPatterns === !options?.patterns) &&
  // if options.events is false we can reuse quote with events
  (!options?.events || !!quote.events) &&
  // Either has no financialAttachments or require all of them to match
  ((options?.financialAttachments?.length ?? 0) === 0 ||
    options!.financialAttachments!.every((financialAttachment) =>
      quote.financialAttachments?.includes(financialAttachment)
    ))

const doesQuoteAndFetchParamsMatch = ({
  quote,
  ticker,
  instrument,
  timeframe,
}: {
  quote: Quote
  ticker: string
  instrument: Instrument
  timeframe: TIMEFRAME
}) => quote.ticker === ticker && quote.instrument === instrument && quote.timeframe === timeframe

// either we don't care about matching uuid at all, or they have to match
const isSameOrNoChartUuid = ({ quote, uuid }: { quote: Quote; uuid?: string }) => !uuid || quote.chartUuid === uuid

const isCacheAllowed = ({ quote, options }: ICacheAvailabilityCheckFunctionsArguments) =>
  !options?.cachePredicate || options.cachePredicate(quote)

const isNotEmptyQuote = (quote: Quote) => quote.fetchedAt !== -1 || quote.getIsFetching() || !!quote.is404

export type QuoteGetParams = {
  ticker: string
  instrument: Instrument
  timeframe: TIMEFRAME
  options?: QuoteFetchOptions & QuoteInternalOptions
  abortController?: null | AbortController
  shouldUseCache?: boolean
  canBeEmptyQuote?: boolean
  chartUuid?: string
}

function getChartEventsWithId({
  ticker,
  chartEvents,
  updateOhlcVersion,
  updateChartEventsVersion,
}: {
  ticker: string
  chartEvents?: ChartEventDataRaw[]
  updateOhlcVersion?: number
  updateChartEventsVersion?: number
}) {
  return (
    chartEvents?.map((item) => ({
      ...item,
      elementId: `${ticker.toLowerCase()}-${item.eventType}-${item.dateTimestamp}`,
      updateOhlcVersion,
      updateChartEventsVersion,
    })) ?? []
  )
}

class Quote extends Spine.Model {
  static computableKeys = [
    'isIntraday',
    'interval',
    'barIndex',
    'barToDataIndex',
    'alignedFinancialAttachmentsData',
    'timestampToFinancialsIndexesMap',
    '_virtualTradedDates',
  ]

  static initClass() {
    this.configure(
      'Quote',
      'ticker',
      'name',
      'instrument',
      'timeframe',
      'open',
      'high',
      'low',
      'close',
      'date',
      'volume',
      'lastOpen',
      'lastHigh',
      'lastLow',
      'lastClose',
      'lastDate',
      'lastVolume',
      'lastTime',
      'dataId',
      'prevClose',
      'COTs',
      'chartEvents',
      'afterClose',
      'afterChange',
      'afterTime',
      'drawMinutesPerDay',
      'marketStartMinutes',
      'premarketLengthMinutes',
      'aftermarketLengthMinutes',
      'aftermarket',
      'premarket',
      'events',
      'financialAttachments',
      'financialAttachmentsData',
      'hasPatterns',
      'patterns',
      'patternsMinRange',
      'patternsMaxRange',
      'relativeVolume',
      'fetchedAt',
      'fetchingPromise',
      'fetchingState',
      'wasFetchAborted',
      'chartUuid',
      'ideaID',
      'updateOhlcVersion',
      'lastRefetch',
      'is404',
      'hasError',
      'hasReachedEnd',
      ...Quote.computableKeys
    )
  }

  static getFromCacheSync({
    ticker: tickerRaw,
    instrument,
    timeframe,
    options,
    chartUuid,
    canBeEmptyQuote,
  }: Omit<QuoteGetParams, 'abortController' | 'shouldUseCache'>): Quote | null {
    const ticker = tickerRaw.replace('@', '')
    return (
      Quote.select<Quote>(
        (q) =>
          doesQuoteAndFetchParamsMatch({ quote: q, ticker, instrument, timeframe }) &&
          isSameOrNoChartUuid({ quote: q, uuid: chartUuid }) &&
          (canBeEmptyQuote || isNotEmptyQuote(q)) &&
          isCacheAllowed({ quote: q, options }) &&
          (q.instrument !== Instrument.Stock ||
            Number.isInteger(q.ideaID) ||
            doesQuoteAndOptionsMatch({ quote: q, options }))
      )[0] ?? null
    )
  }

  static async get({
    ticker: tickerRaw,
    instrument,
    timeframe,
    options,
    abortController,
    shouldUseCache = true,
    canBeEmptyQuote = false,
    chartUuid,
  }: QuoteGetParams): Promise<Quote> {
    const ticker = tickerRaw.replace('@', '')
    const quote = shouldUseCache
      ? this.getFromCacheSync({ ticker: tickerRaw, instrument, timeframe, options, chartUuid, canBeEmptyQuote })
      : null

    if (quote) {
      if (quote.getIsFetching()) {
        await quote.getResolvedFetchingPromise()
      } else if (options?.fetchNewDataOnCachedQuote) {
        await quote.fetchData({ fetchType: QuoteFetchType.NewerData, abortController })
      }
      return quote
    }

    const newQuote = Quote.create<Quote>({
      ...getEmptyQuote(),
      ticker,
      timeframe,
      instrument,
      chartUuid,
      premarket: options?.premarket ?? 0,
      aftermarket: options?.aftermarket ?? 0,
      hasPatterns: !!options?.patterns,
      events: !!options?.events,
      financialAttachments: options?.financialAttachments ?? [],
    })

    await newQuote.fetchData({
      fetchType: QuoteFetchType.Refetch,
      abortController,
      options: {
        dateFrom: options?.dateFrom,
        dateTo: options?.dateTo,
        barsCount: options?.barsCount,
        leftBuffer: options?.leftBuffer,
      },
    })

    return newQuote
  }

  static async getAll(
    tickersAndTimeframes: Array<Pick<QuoteGetParams, 'ticker' | 'instrument' | 'timeframe' | 'options'>>
  ) {
    // TODO: fetch in 1 request
    if (tickersAndTimeframes.length === 0) {
      return {}
    }

    try {
      const quotes = await Promise.all(tickersAndTimeframes.map((props) => this.get(props)))
      const data = quotes.reduce(
        (accumulator, quote) => ({
          ...accumulator,
          [quote.ticker]: quote,
        }),
        {}
      )
      return data
    } catch {
      // TODO: handle somehow
    }
  }

  declare ticker: string
  declare name: string
  declare instrument: Instrument
  declare interval: number
  declare timeframe: TIMEFRAME
  declare open: number[]
  declare high: number[]
  declare low: number[]
  declare close: number[]
  declare date: number[]
  declare volume: number[]
  declare lastOpen: number
  declare lastHigh: number
  declare lastLow: number
  declare lastClose: number
  declare lastDate: number
  declare lastTime: number
  declare lastVolume: number
  declare dataId: string
  declare prevClose: number
  declare COTs: COTData
  declare afterClose: number
  declare afterChange: number
  declare drawMinutesPerDay: number
  declare marketStartMinutes: number
  declare premarketLengthMinutes: number
  declare aftermarketLengthMinutes: number
  declare hasPatterns: boolean
  declare patterns: Array<TodoPattern>
  declare patternsMinRange: number
  declare patternsMaxRange: number
  declare relativeVolume: number
  declare isIntraday: boolean
  declare hasReachedEnd: boolean
  declare barIndex: number[]
  /**
   * Array of length the same as number of bars
   *
   * If there's a gap and a bar index has no corresponding data index,
   * the last data index is used (or 0 if there's no last data index)
   *
   * @type {number[]}
   */
  declare barToDataIndex: number[]
  declare chartUuid?: string
  declare aftermarket: number
  declare premarket: number
  declare fetchedAt: number
  declare fetchingState: QuoteFetchType[]
  declare fetchingPromise?: Promise<void>
  declare wasFetchAborted: boolean
  declare ideaID?: number
  declare updateOhlcVersion?: number
  declare updateChartEventsVersion?: number
  declare lastRefetch: number
  declare is404?: boolean
  declare hasError?: boolean
  declare chartEvents: Array<IEarnings | IDividends | ISplit>
  declare events?: boolean
  declare financialAttachments?: QuoteFinancialAttachmentType[]
  declare financialAttachmentsData?: FinancialAttachmentsData
  declare alignedFinancialAttachmentsData: FinancialAttachmentsData

  declare timestampToFinancialsIndexesMap: Partial<ObjectHash<ObjectHash<number[]>, QuoteFinancialAttachmentType>>
  declare private _virtualTradedDates: {
    nextCacheKey: null | string
    prevCacheKey: null | string
    next: number[]
    prev: number[]
  }

  async fetchRequest({
    fetchType,
    abortController,
    options = {},
  }: {
    fetchType: QuoteFetchType
    abortController?: null | AbortController
    options?: QuoteUrlOptions
  }) {
    let data: DataResponse | null = null
    let is404 = false
    let hasError = false
    let wasFetchAborted = false

    const fetchingPromise = fetchApi<DataResponse>({
      location: this.getUrl({ dataId: fetchType === QuoteFetchType.NewerData ? this.dataId : undefined, ...options }),
      throwOnStatusCodes: [404],
      abortController,
    })

    this.updateAttributes({
      fetchingState: [...this.fetchingState, fetchType],
      fetchingPromise,
    })

    try {
      data = await fetchingPromise
    } catch (err) {
      is404 = err instanceof NotFoundError
      if (getIsAbortError(err as Error)) {
        wasFetchAborted = true
      } else {
        hasError = true
        if (!is404 && process.env.IS_E2E_TESTING) {
          throw new Error('Quote fetch error', { cause: err })
        }
      }
      // Ignore network/notfound errors
    }

    const shouldUseEmptyQuote = !data && !wasFetchAborted && fetchType === QuoteFetchType.Refetch

    this.updateAttributes({
      ...(shouldUseEmptyQuote ? getEmptyQuote() : {}),
      is404,
      hasError,
      wasFetchAborted,
      fetchingState: this.fetchingState.filter((type) => type !== fetchType),
    })

    return data
  }

  getIsFetching(fetchType?: QuoteFetchType | QuoteFetchType[]) {
    if (fetchType === undefined) return this.fetchingState.length > 0

    const fetchTypeArray = Array.isArray(fetchType) ? fetchType : [fetchType]
    return this.fetchingState.some((type) => fetchTypeArray.includes(type))
  }

  async isPossibleToFetchSequentialData({
    abortController,
  }: {
    abortController?: null | AbortController
  } = {}) {
    const data = await this.fetchRequest({
      fetchType: QuoteFetchType.NewerData,
      abortController,
      options: {
        dateFrom: this.date[this.date.length - 2],
        barsCount: 1,
        dataId: undefined,
      },
    })
    if (data && data.date.length > 0 && this.date.length > 0) {
      return this.date[this.date.length - 1] >= data.date[0]
    }
    return false
  }

  async fetchData({
    fetchType,
    abortController,
    options = {},
  }: {
    fetchType: QuoteFetchType
    abortController?: null | AbortController
    options?: QuoteUrlOptions
  }): Promise<void> {
    const isFuturesForcedRefetch =
      fetchType === QuoteFetchType.NewerData &&
      this.instrument === Instrument.Futures &&
      this.lastRefetch < Date.now() - FUTURES_FORCE_REFETCH_TIMEOUT
    const isRefetch = [QuoteFetchType.Refetch, QuoteFetchType.DataChangeRefetch].includes(fetchType)
    const isFetchOlder = fetchType === QuoteFetchType.Older

    if (this.is404 && !isRefetch) {
      return
    }

    if (isFuturesForcedRefetch) {
      return this.dataChangeRefetch(abortController)
    } else if (isRefetch) {
      options.dateFrom = options.dateFrom ?? this.date[0]
    } else if (fetchType === QuoteFetchType.NewerData) {
      if (this.date.length >= 2) {
        options.dateFrom = this.date[this.date.length - 2]
      }
    } else if (isFetchOlder) {
      options.dateTo = this.date[0]
    }

    const data = await this.fetchRequest({ fetchType, abortController, options })

    if (!data || data.noNewData === true) {
      return
    }
    if (!isRefetch && this.instrument === Instrument.Stock && this.updateOhlcVersion !== data.updateOhlcVersion) {
      return this.dataChangeRefetch(abortController)
    }
    if (isFetchOlder && this.date.length > 0 && !data?.date.some((date) => date < this.date[0])) {
      this.updateAttributes({ hasReachedEnd: true })
      return
    }
    if (this.timeframe !== data.timeframe || this.ticker.toLowerCase() !== data.ticker?.toLowerCase()) {
      return
    }

    const newQuoteData = {
      COTs: isRefetch ? data.COTs : this.COTs,
      name: data.name,
      open: data.open,
      high: data.high,
      low: data.low,
      close: data.close,
      volume: data.volume,
      date: data.date,
      drawMinutesPerDay: data.drawMinutesPerDay,
      marketStartMinutes: data.marketStartMinutes,
      premarketLengthMinutes: data.premarketLengthMinutes,
      aftermarketLengthMinutes: data.aftermarketLengthMinutes,
      patterns: data.patterns ?? [],
      patternsMinRange: data.patternsMinRange,
      patternsMaxRange: data.patternsMaxRange,
      relativeVolume: data.relativeVolume,
      lastOpen: data.lastOpen,
      lastHigh: data.lastHigh,
      lastLow: data.lastLow,
      lastClose: data.lastClose,
      lastVolume: data.lastVolume,
      dataId: fetchType === QuoteFetchType.NewerData || isRefetch ? data.dataId : this.dataId,
      lastDate: data.lastDate,
      lastTime: data.lastTime,
      prevClose: data.prevClose,
      afterClose: data.afterClose,
      afterChange: data.afterChange,
      afterTime: data.afterTime,
      numberOfRefreshChecks: 0,
      updateOhlcVersion: this.updateOhlcVersion,
      updateChartEventsVersion: this.updateChartEventsVersion ?? 0,
      lastRefetch: isRefetch ? Date.now() : this.lastRefetch,
      chartEvents: isRefetch ? (data.chartEvents ?? []) : this.chartEvents,
      fetchedAt: Date.now(),
      financialAttachmentsData: isRefetch ? data.financialAttachmentsData : this.financialAttachmentsData,
      hasReachedEnd:
        isRefetch || isFetchOlder
          ? options.barsCount === undefined && options.dateFrom === undefined && options.leftBuffer === undefined
          : this.hasReachedEnd,
    }

    if (!isRefetch) {
      /*
       * Merges chart events
       * Adds new chart events and overwrites chart events with new ones if they are from the same day
       */
      if ((data.chartEvents?.length ?? 0) > 0) {
        newQuoteData.chartEvents = [
          ...this.chartEvents.filter(
            ({ dateTimestamp, eventType }) =>
              !data.chartEvents?.some(
                (chartEvent) =>
                  isSameDay(dateFromUnixTimestamp(chartEvent.dateTimestamp), dateFromUnixTimestamp(dateTimestamp)) &&
                  chartEvent.eventType === eventType
              )
          ),
          ...(data.chartEvents ?? []),
        ].sort((a, b) => {
          if (a.dateTimestamp === b.dateTimestamp) {
            return a.eventType.localeCompare(b.eventType)
          }
          return a.dateTimestamp - b.dateTimestamp
        })

        if (JSON.stringify(this.chartEvents) !== JSON.stringify(newQuoteData.chartEvents)) {
          newQuoteData.updateChartEventsVersion += 1
        }
      }
      /*
       * Merges financial attachments
       * Adds new financial attachments and overwrites financial attachments with new ones if they have the same timestamp
       */
      Object.keys(data.financialAttachmentsData ?? {}).forEach(
        <T extends keyof FinancialAttachmentsData>(keyString: string) => {
          const key = keyString as T
          const financialAttachment = data.financialAttachmentsData![key]

          if (!financialAttachment?.length) return

          newQuoteData.financialAttachmentsData ??= {}
          newQuoteData.financialAttachmentsData[key] = [
            ...(this.financialAttachmentsData?.[key]?.filter(
              ({ timestamp }) => !financialAttachment.some((item) => timestamp === item.timestamp)
            ) ?? []),
            ...financialAttachment,
          ].sort((item1, item2) => item1.timestamp - item2.timestamp) as FinancialAttachmentsData[T]
        }
      )
      /*
       * Merges COTs
       */
      const COTKeys = this.instrument === Instrument.Futures ? Object.keys(data.COTs) : []
      if (COTKeys.length > 0) {
        for (
          let i = isFetchOlder ? data.date.length - 1 : 0;
          isFetchOlder ? i >= 0 : i < data.date.length;
          isFetchOlder ? i-- : i++
        ) {
          const date = data.date[i]
          if (this.date.includes(date)) {
            continue
          }
          COTKeys.forEach((key) => {
            COTProps.forEach((prop) => {
              if (isFetchOlder) {
                newQuoteData.COTs[key][prop].unshift(data.COTs[key][prop][i])
              } else {
                newQuoteData.COTs[key][prop].push(data.COTs[key][prop][i])
              }
            })
          })
        }
      }
      /*
       * Merges current and new data taking date as index
       */
      let AIndex = 0
      let BIndex = 0
      let mergeIndex = -1
      while (AIndex < this.date.length || BIndex < data.date.length) {
        const ADate = this.date[AIndex]
        const BDate = data.date[BIndex]
        if (BDate === undefined) {
          break
        } else if (ADate === BDate) {
          // Same date, update OHLCV
          this.open[AIndex] = data.open[BIndex]
          this.high[AIndex] = data.high[BIndex]
          this.low[AIndex] = data.low[BIndex]
          this.close[AIndex] = data.close[BIndex]
          this.volume[AIndex] = data.volume[BIndex]
          AIndex++
          BIndex++
        } else if (ADate > BDate || ADate === undefined) {
          // New bar for a date not yet in our dataset
          // If weekly or monthly timeframe & wasn't merged on AIndex
          if (mergeIndex === -1 && [TIMEFRAME.w, TIMEFRAME.m].includes(this.timeframe)) {
            const prevAIndex = AIndex - 1
            const prevDate = dateFromUnixTimestamp(this.date[prevAIndex])
            const newDate = dateFromUnixTimestamp(data.date[BIndex])
            // If merge bar for weekly is sameWeek or for monthly is sameMonth perform merge on AIndex
            if (
              (TIMEFRAME.w === this.timeframe && isSameWeek(prevDate, newDate, { weekStartsOn: 1 })) ||
              (TIMEFRAME.m === this.timeframe && isSameMonth(prevDate, newDate))
            ) {
              this.date[prevAIndex] = data.date[BIndex]
              this.open[prevAIndex] = data.open[BIndex]
              this.high[prevAIndex] = data.high[BIndex]
              this.low[prevAIndex] = data.low[BIndex]
              this.close[prevAIndex] = data.close[BIndex]
              this.volume[prevAIndex] = data.volume[BIndex]
              mergeIndex = prevAIndex
              // Incrementing BIndex only because we're mergin arrays on AIndex
              BIndex++
              continue
            }
          }
          this.date.splice(AIndex, 0, data.date[BIndex])
          this.open.splice(AIndex, 0, data.open[BIndex])
          this.high.splice(AIndex, 0, data.high[BIndex])
          this.low.splice(AIndex, 0, data.low[BIndex])
          this.close.splice(AIndex, 0, data.close[BIndex])
          this.volume.splice(AIndex, 0, data.volume[BIndex])
          // Incrementing AIndex because we're changing the array in-place
          AIndex++
          BIndex++
        } else if (ADate < BDate) {
          AIndex++
        } else {
          console.warn('fetchNewerData merge warning')
          window.Sentry?.captureMessage('quote.ts fetchNewerData merge warning', {
            extra: {
              thisDateLength: this.date.length,
              dataDateLength: data.date.length,
              AIndex,
              BIndex,
              ADate,
              BDate,
            },
          })
        }
      }

      newQuoteData.date = this.date
      newQuoteData.open = this.open
      newQuoteData.high = this.high
      newQuoteData.low = this.low
      newQuoteData.close = this.close
      newQuoteData.volume = this.volume
    }

    newQuoteData.updateOhlcVersion = data.updateOhlcVersion

    const hasUpdatedSuccessfully = this.updateAttributes(newQuoteData)
    // https://github.com/finvizhq/charts/issues/510
    if (!hasUpdatedSuccessfully) {
      // attempt to find if this.model.quote() => null is comming from this.save() validation fail
      window.Sentry?.captureMessage('quote.ts fetchNewerData save() validation failed', { extra: this })
    }
    if (!this) {
      // or this somehow became null / falsy
      window.Sentry?.captureMessage('quote.ts fetchNewerData this is falsy', { extra: this })
    }
    this.trigger(fetchType, this, fetchType)
  }

  async getResolvedFetchingPromise() {
    try {
      return await this.fetchingPromise
    } catch {
      // do nothing
    }
  }

  fetchOlderData(options?: QuoteFetchOptions) {
    return this.fetchData({ fetchType: QuoteFetchType.Older, options })
  }

  dataChangeRefetch(abortController?: null | AbortController) {
    return this.fetchData({
      fetchType: QuoteFetchType.DataChangeRefetch,
      abortController,
      options: {
        dateFrom: this.date[0],
      },
    })
  }

  load(atts: ObjectHash) {
    super.load(atts)
    // This has to be here to prevent recalculations if atts.fetchedAt is not provided
    const fetchedAt = atts.fetchedAt ?? this.fetchedAt ?? 0
    const { cachedAt } = (getCachedQuote({ cid: this.cid }) as { cachedAt: number } | null) || {}
    if (this.timeframe && cachedAt !== fetchedAt) {
      setCachedQuote({ cid: this.cid, key: 'cachedAt', value: fetchedAt, fetchedAt })
      this.isIntraday = [
        TIMEFRAME.i1,
        TIMEFRAME.i2,
        TIMEFRAME.i3,
        TIMEFRAME.i5,
        TIMEFRAME.i10,
        TIMEFRAME.i15,
        TIMEFRAME.i30,
        TIMEFRAME.h,
        TIMEFRAME.h2,
        TIMEFRAME.h4,
      ].includes(this.timeframe)
      this.interval = this.isIntraday
        ? IntradayTimeframeInterval[this.timeframe as keyof typeof IntradayTimeframeInterval]
        : 0
      this.barIndex = this.getBarIndexes()
      this.patterns = this.patterns ?? []
      this.barToDataIndex = this.barIndex.flatMap((barIndex, index) => {
        const lastIndex = this.barIndex[index - 1] ?? -1
        return [...Array.from({ length: barIndex - lastIndex - 1 }).fill(Math.max(0, index - 1)), index] as number[]
      })
      this.alignAllFinancialAttachmentsDataWithQuoteDates()
      this.getVirtualTradedDates()
    }
    if (this.timeframe && this.ticker) {
      this.chartEvents = getChartEventsWithId({
        updateOhlcVersion: this.updateOhlcVersion,
        updateChartEventsVersion: this.updateChartEventsVersion,
        ticker: this.ticker,
        chartEvents: this.chartEvents as unknown as ChartEventDataRaw[],
      })
    }

    this.fetchingState ??= []

    return this
  }

  alignAllFinancialAttachmentsDataWithQuoteDates() {
    this.alignedFinancialAttachmentsData = {}
    this.timestampToFinancialsIndexesMap = {}
    this.financialAttachments?.forEach((quoteFinancialAttachment) => {
      this.alignFinancialAttachmentsDataWithQuoteDates(quoteFinancialAttachment)
    })
  }

  alignFinancialAttachmentsDataWithQuoteDates<T extends QuoteFinancialAttachmentType>(quoteFinancialAttachment: T) {
    const timestampToFinancialsIndexesMap: ObjectHash<number[]> = {}
    const financialAttachmentsData = this.financialAttachmentsData?.[quoteFinancialAttachment]

    if (financialAttachmentsData) {
      let lastDateIndex = 0
      const firstDate = this.date[0]
      this.alignedFinancialAttachmentsData[quoteFinancialAttachment] = financialAttachmentsData.map((data, index) => {
        let matchingQuoteTimestamp: number | null = null
        let isVirtualMatch = false

        const parsedDate = dateFromUnixTimestamp(data.timestamp)
        const convertedDateToNyTimestamp = convertLocalToNyTime(parsedDate, false).getTime() / 1000

        if (firstDate < convertedDateToNyTimestamp) {
          let previousDailyTimestamp: number | null = null
          for (let dateIndex = lastDateIndex; dateIndex < this.date.length; dateIndex += 1) {
            const timestamp = this.date[dateIndex]
            const dateDate = dateFromUnixTimestamp(timestamp)
            if (this.isIntraday || this.timeframe === TIMEFRAME.d) {
              const isBetweenDates =
                previousDailyTimestamp !== null && previousDailyTimestamp < data.timestamp && timestamp > data.timestamp
              const areDatesSameDay = isSameDay(parsedDate, dateDate)
              if (areDatesSameDay || isBetweenDates) {
                lastDateIndex = dateIndex
                matchingQuoteTimestamp = timestamp
                isVirtualMatch = isBetweenDates && !areDatesSameDay
                break
              } else {
                previousDailyTimestamp = timestamp
              }
            } else if (this.timeframe === TIMEFRAME.w) {
              if (isSameWeek(parsedDate, dateDate, { weekStartsOn: 1 })) {
                lastDateIndex = dateIndex
                matchingQuoteTimestamp = timestamp
                break
              }
            } else if (this.timeframe === TIMEFRAME.m) {
              if (isSameMonth(parsedDate, dateDate)) {
                lastDateIndex = dateIndex
                matchingQuoteTimestamp = timestamp
                break
              }
            }
          }
        }

        if (matchingQuoteTimestamp !== null) {
          const key = matchingQuoteTimestamp.toString()
          timestampToFinancialsIndexesMap[key] ??= []
          timestampToFinancialsIndexesMap[key].push(index)
        }

        return { ...data, matchingQuoteTimestamp, isVirtualMatch }
      }) as FinancialAttachmentsData[T]

      this.timestampToFinancialsIndexesMap[quoteFinancialAttachment] = timestampToFinancialsIndexesMap
    }
  }

  clearCachedData() {
    deleteCachedQuote({ cid: this.cid })
  }

  destroy(options?: ObjectHash) {
    super.destroy(options)
    this.clearCachedData()
    return this
  }

  getHeikinAshiData() {
    const cachedHeikinAshiData = getCachedQuote({ cid: this.cid, key: 'heikinAshiData', fetchedAt: this.fetchedAt })
    if (cachedHeikinAshiData) {
      return cachedHeikinAshiData as unknown as { open: number[]; close: number[] }
    }

    const HAClose = this.open.map((_, i) => (this.open[i] + this.high[i] + this.close[i] + this.low[i]) / 4)
    const HAOpen = this.open.reduce(
      (acc, open, i) => [...acc, (i === 0 ? open + HAClose[0] : acc[i - 1] + HAClose[i - 1]) / 2],
      [] as number[]
    )

    const heikAshiData = { open: HAOpen, close: HAClose }

    setCachedQuote({ cid: this.cid, key: 'heikinAshiData', value: heikAshiData, fetchedAt: this.fetchedAt })

    return heikAshiData
  }

  getDayToOffset(): ObjectHash<number> {
    const cachedDayToOffset = getCachedQuote({ cid: this.cid, key: 'dayToOffset', fetchedAt: this.fetchedAt })
    if (cachedDayToOffset) {
      return cachedDayToOffset as unknown as ObjectHash<number>
    }
    const dayToOffset: ObjectHash = {}
    let day = 0
    let lastDay
    for (let i = 0, end = this.date.length, asc = end >= 0; asc ? i < end : i > end; asc ? i++ : i--) {
      const date = dateFromUnixTimestamp(this.date[i])
      const dateString = dateStringFromDate(date)
      if (dateString !== lastDay) {
        dayToOffset[dateString] = day
        day++
        lastDay = dateString
      }
    }
    // for ofscreen renderer we need to add lastDate to have correct dayToOffset because if
    // premarket=false in fetch query there isn't other way to distinguish that
    if (getIsSSr() && this.lastDate) {
      // YYYYMMDD / 20211118 => YYYY-MM-DD
      const [yyyy, mm, dd] = [~~(this.lastDate / 10000), ~~(this.lastDate / 100) % 100, this.lastDate % 100]
      const lastDate = `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`

      if (lastDay !== lastDate) {
        dayToOffset[lastDate] = day
      }
    }

    setCachedQuote({ cid: this.cid, key: 'dayToOffset', value: dayToOffset, fetchedAt: this.fetchedAt })
    return dayToOffset
  }

  getDaysInQuote() {
    const cachedDayToOffset = getCachedQuote({ cid: this.cid, key: 'daysInQuote', fetchedAt: this.fetchedAt })
    if (cachedDayToOffset) {
      return cachedDayToOffset as unknown as Date[]
    }
    const dayToOffset = this.getDayToOffset()
    const daysInQuote = Object.keys(dayToOffset)
      .map((dateString) => {
        const [year, month, day] = dateString.split('-')
        return new Date(Number.parseInt(year), Number.parseInt(month) - 1, Number.parseInt(day))
      })
      .sort((a, b) => a.getTime() - b.getTime())

    setCachedQuote({ cid: this.cid, key: 'daysInQuote', value: daysInQuote, fetchedAt: this.fetchedAt })
    return daysInQuote
  }

  getBarIndexes() {
    if (!this.isIntraday || this.instrument !== Instrument.Stock) {
      return Array.from({ length: this.date.length }).map((_, index) => index)
    }

    const barIndex = []
    const dayToOffset = this.getDayToOffset()

    for (let i = 0; i < this.date.length; i += 1) {
      const date = dateFromUnixTimestamp(this.date[i])
      let x = Math.ceil((date.getHours() * 60 + date.getMinutes() - this.marketStartMinutes) / this.interval)
      x += dayToOffset[dateStringFromDate(date)] * this.getBarsInDay() // add days
      barIndex[i] = x
    }

    return barIndex
  }

  getBarIndex(i: number) {
    if (!this.isIntraday || this.instrument !== Instrument.Stock) {
      return i
    }
    return this.barIndex[i]
  }

  getBarsInDay() {
    if (!this.isIntraday || this.instrument !== Instrument.Stock || !this.drawMinutesPerDay || !this.interval) {
      return 0
    }
    return Math.ceil(this.drawMinutesPerDay / this.interval)
  }

  getDataIndexByBarIndex(index: number) {
    if (this.barIndex) {
      for (let i = 0; i < this.barIndex.length; i++) {
        if (this.barIndex[i] === index) {
          return i
        }
      }
    }
    return -1
  }

  getDataByBarIndex(key: keyof Quote, index: number) {
    const dataIndex = this.getDataIndexByBarIndex(index)
    if (dataIndex > -1) {
      const quoteData = this[key as keyof Quote]! as number[]
      return quoteData[dataIndex] ?? null
    }
    return null
  }

  getDateToIndex() {
    const cachedDateToIndex = getCachedQuote({ cid: this.cid, key: 'dateToIndex', fetchedAt: this.fetchedAt })
    if (cachedDateToIndex) {
      return cachedDateToIndex as unknown as ObjectHash<number>
    }
    const dateToIndex: ObjectHash<number> = {}
    for (let i = 0; i < this.date.length; i++) {
      dateToIndex[this.date[i]] = i
    }
    setCachedQuote({ cid: this.cid, key: 'dateToIndex', value: dateToIndex, fetchedAt: this.fetchedAt })
    return dateToIndex
  }

  getVirtualTradedDates() {
    this._virtualTradedDates ??= { ...DEFAULT_VIRTUAL_TRADED_DATES }
    const extraDates = 100
    const absPositionX =
      getIsSSr() || this.instrument === Instrument.MarketSentiment ? 0 : window.innerWidth + extraDates
    const minPrevRequired = absPositionX - extraDates

    const dateLength = this.date.length
    const commonCacheKey = `${this.ticker}-${this.timeframe}-${this.drawMinutesPerDay ?? 0}`
    const prevCacheKey = dateLength > 0 ? `${commonCacheKey}-${this.date[0]}` : null
    const nextCacheKey = dateLength > 0 ? `${commonCacheKey}-${this.date[dateLength - 1]}` : null
    const hasEnoughPrevDates = dateLength > 0 ? this._virtualTradedDates.prev.length >= minPrevRequired : true
    const isPrevCacheKeyChanged = this._virtualTradedDates.prevCacheKey !== prevCacheKey

    if (isPrevCacheKeyChanged || !hasEnoughPrevDates) {
      const prevVirtualDate = getHistoryTradingDates({
        quote: this,
        positionX: Math.ceil(absPositionX - 1),
      }).map((date) => convertLocalToNyTime(date, false).getTime() / 1000)
      this.updateAttributes({
        _virtualTradedDates: {
          ...this._virtualTradedDates,
          prevCacheKey,
          prev: prevVirtualDate,
        },
      })
    }

    if (this._virtualTradedDates.nextCacheKey !== nextCacheKey || isPrevCacheKeyChanged || !hasEnoughPrevDates) {
      const nextVirtualDate = getFutureTradingDates({
        quote: this,
        positionX: Math.ceil(absPositionX + this.barIndex[this.barIndex.length - 1]),
      }).map((date) => convertLocalToNyTime(date, false).getTime() / 1000)

      this.updateAttributes({
        _virtualTradedDates: {
          ...this._virtualTradedDates,
          nextCacheKey,
          next: nextVirtualDate,
        },
      })
    }

    return this._virtualTradedDates
  }

  getVirtualBarTimeframeInSeconds(timeframe: TIMEFRAME) {
    const { prev: prevDates, next: nextDates } = this.getVirtualTradedDates()

    if (prevDates.length > 0 && nextDates.length > 0) {
      return (
        (nextDates[nextDates.length - 1] - prevDates[prevDates.length - 1]) / (nextDates.length + prevDates.length - 1)
      )
    }
    return TIMEFRAME_SECONDS[timeframe]
  }

  getTimestampFomPositionX(positionX: number) {
    if (this.date.length === 0) {
      return 0
    }
    const { prev: prevDates, next: nextDates } = this.getVirtualTradedDates()
    const timeframeSeconds = this.getVirtualBarTimeframeInSeconds(this.timeframe)
    const lastNextDate = nextDates[nextDates.length - 1]
    const lastPrevDate = prevDates[prevDates.length - 1] ?? nextDates[0]

    if (positionX < 0) {
      const absPositionX = Math.abs(positionX)
      const positionXFraction = absPositionX % 1
      const lowerPositionX = Math.floor(absPositionX - 1)
      const lowerVirtualDateTimestamp =
        prevDates[lowerPositionX] ?? lastPrevDate - (lowerPositionX - (prevDates.length - 1)) * timeframeSeconds

      if (positionXFraction > 0) {
        const upperPositionX = lowerPositionX + 1
        const upperVirtualDateTimestamp = prevDates[upperPositionX] ?? lowerVirtualDateTimestamp - timeframeSeconds

        const timestampDiff = (upperVirtualDateTimestamp - lowerVirtualDateTimestamp) * positionXFraction
        return lowerVirtualDateTimestamp + timestampDiff
      }

      return lowerVirtualDateTimestamp
    }

    const lowerPositionX = Math.floor(positionX)
    const positionXFraction = positionX % 1
    const lowerVirtualDateTimestamp =
      nextDates[lowerPositionX] ?? lastNextDate + (lowerPositionX - (nextDates.length - 1)) * timeframeSeconds

    if (positionXFraction > 0) {
      const upperPositionX = lowerPositionX + 1
      const upperVirtualDateTimestamp = nextDates[upperPositionX] ?? lowerVirtualDateTimestamp + timeframeSeconds

      const timestampDiff = (upperVirtualDateTimestamp - lowerVirtualDateTimestamp) * positionXFraction
      return lowerVirtualDateTimestamp + timestampDiff
    }

    return lowerVirtualDateTimestamp
  }

  getPositionXFromTimestamp(timestamp: number) {
    if (this.date.length === 0) {
      return 0
    }
    const { prev: prevDates, next: nextDates } = this.getVirtualTradedDates()
    const timeframeSeconds = this.getVirtualBarTimeframeInSeconds(this.timeframe)
    const firstDate = nextDates[0]

    if (timestamp < firstDate) {
      const lastVirtualDateIndex = prevDates.length - 1
      const lastTimestamp = prevDates[lastVirtualDateIndex]
      const lowerBoundIndex = lastTimestamp <= timestamp ? prevDates.findIndex((time) => time <= timestamp) : -1
      const lowerBoundTimestamp = prevDates[lowerBoundIndex]

      if (lowerBoundIndex === -1) {
        const posXDiff = (lastTimestamp - timestamp) / timeframeSeconds

        return -(lastVirtualDateIndex + posXDiff + 1)
      } else if (lowerBoundTimestamp === timestamp) {
        return -(lowerBoundIndex + 1)
      }

      const upperBoundTimestamp = lowerBoundIndex === 0 ? nextDates[0] : prevDates[lowerBoundIndex - 1]
      const posXDiff = (timestamp - lowerBoundTimestamp) / (upperBoundTimestamp - lowerBoundTimestamp)

      return -(lowerBoundIndex - posXDiff) - 1
    }

    const lastVirtualDateIndex = nextDates.length - 1
    const lastTimestamp = nextDates[lastVirtualDateIndex]
    const upperBoundIndex = lastTimestamp >= timestamp ? nextDates.findIndex((time) => time >= timestamp) : -1
    const upperBoundTimestamp = nextDates[upperBoundIndex]

    if (upperBoundIndex === -1) {
      const posXDiff = (timestamp - lastTimestamp) / timeframeSeconds
      return lastVirtualDateIndex + posXDiff
    } else if (upperBoundTimestamp === timestamp) {
      return upperBoundIndex
    }

    const lowerBoundTimestamp = upperBoundIndex === 0 ? prevDates[0] : nextDates[upperBoundIndex - 1]
    const posXDiff = (upperBoundTimestamp - timestamp) / (upperBoundTimestamp - lowerBoundTimestamp)

    return upperBoundIndex - posXDiff
  }

  getUrl(options: QuoteUrlOptions) {
    const config: UrlOptions = {
      ticker: this.ticker,
      instrument: this.instrument,
      timeframe: this.timeframe || 'd1',
      aftermarket: this.aftermarket,
      premarket: this.premarket,
      patterns: this.hasPatterns,
      events: this.events == null ? this.instrument === Instrument.Stock : this.events,
      ...options,
      financialAttachments: this.financialAttachments?.join(','),
    }

    const isBrowserElite = !getIsSSr() && document.location.host === 'elite.finviz.com'
    let domain = isBrowserElite ? 'https://api.finviz.com' : ''

    if (getIsSSr()) {
      domain = process.env.BE_API_URL!
    }

    return `${domain}/api/quote.ashx?${queryString.stringify({ rev: Date.now(), ...config })}`
  }

  getRawTicker() {
    return getRawTickerForInstrument(this.instrument, this.ticker)
  }

  clearData() {
    this.updateAttributes(getEmptyQuote())
  }

  toConfig<T extends Partial<RootChartConfigObject> = RootChartConfigObject>(omitKeys = [] as string[]): T {
    return JSON.parse(JSON.stringify(omit(this.toJSON(), [...Quote.computableKeys, ...omitKeys])))
  }
}

Quote.initClass()

export default Quote
